import { useEffect, useMemo, useRef } from "react";
import type { IColors } from "@swiggy-private/dls-web-common/dist/components/types.internal";

import { DEAL_RUSH, MAGIC_ITEMS, WARNING_COLOR } from "../constants";

interface UseItemGroupPriceColorParams {
    itemGroupTagId?: string | null;
    color?: string | null;
}

export const useItemGroupPriceColor = ({
    itemGroupTagId,
    color,
}: UseItemGroupPriceColorParams): keyof IColors | undefined => {
    return useMemo(() => {
        if (itemGroupTagId === DEAL_RUSH) {
            // adding this as price crash uses same tag of deal rush but has different color, earlier rain color was hardcoded for deal rush case
            if (color) {
                return color as keyof IColors;
            }
            return "rain";
        }
        if (itemGroupTagId === MAGIC_ITEMS && color) {
            return color as keyof IColors;
        }
        return undefined;
    }, [itemGroupTagId, color]);
};

export const useItemGroupOfferLimitColor = ({ itemGroupTagId, color }: UseItemGroupPriceColorParams): keyof IColors => {
    return useMemo((): keyof IColors => {
        if (itemGroupTagId === MAGIC_ITEMS && color) {
            return color as keyof IColors;
        }
        return WARNING_COLOR as keyof IColors;
    }, [itemGroupTagId, color]);
};

interface UseMagicItemsOfferLimitImpressionParams {
    disableIncreaseQty?: boolean;
    itemGroupTagId?: string | null;
    menuItemId: number;
    quantity: number;
    onImpressionMagicItemsOfferLimit?: (itemId: string, qty: number) => void;
}

export const useMagicItemsOfferLimitImpression = ({
    disableIncreaseQty,
    itemGroupTagId,
    menuItemId,
    quantity,
    onImpressionMagicItemsOfferLimit,
}: UseMagicItemsOfferLimitImpressionParams): void => {
    const impressionFiredRef = useRef(false);
    const quantityRef = useRef(quantity);

    // Keep quantity ref updated
    quantityRef.current = quantity;

    // Reset impression tracking when menuItemId changes
    useEffect(() => {
        impressionFiredRef.current = false;
    }, [menuItemId]);

    useEffect(() => {
        if (
            disableIncreaseQty &&
            itemGroupTagId === MAGIC_ITEMS &&
            onImpressionMagicItemsOfferLimit &&
            !impressionFiredRef.current
        ) {
            onImpressionMagicItemsOfferLimit(String(menuItemId), quantityRef.current);
            impressionFiredRef.current = true;
        }
    }, [disableIncreaseQty, itemGroupTagId, menuItemId, onImpressionMagicItemsOfferLimit]);
};
